import React, { useMemo } from 'react';
import { Menu, MenuButton, MenuList, MenuItem, Box, Flex, Text } from '@chakra-ui/react';
import ScrollableBox from '../ScrollableBox';

/**
 * options has 3 types:
 * 1) options={[{ value: 1, label:'First' }, { value: 2, label: 'Second' }]}
 * 2) options={{ 1: 'First', 2: 'Second' }}
 * 3) options={[ obj1, obj2, obj3 ]} -- extractor method required.
 */
function Dropdown({
  options: providedOptions,
  disabledOptions = [],
  extractor = null,
  placeholder,
  value,
  onChange,
  isInvalid = false,
  optionsListMaxHeight,
  ...rest
}) {
  const options = useMemo(() => {
    if (Array.isArray(providedOptions)) {
      if (providedOptions.length === 0) {
        return [];
      }

      const fields = Object.keys(providedOptions[0]);
      if (fields.length === 2 && fields.includes('value') && fields.includes('label')) {
        return providedOptions;
      }

      if (typeof extractor !== 'function') {
        throw new Error('Extractor method required for rendering dropdown.');
      }

      return providedOptions.map((item, index) => extractor(item, index));
    }

    return Object.keys(providedOptions).reduce((acc, option) => {
      acc.push({ value: option, label: providedOptions[option] });
      return acc;
    }, []);
  }, [providedOptions, extractor]);

  const selectedIndex = useMemo(() => {
    return options.findIndex((option) => option.value === value);
  }, [value, options]);

  const isDisabled = rest.disabled || rest.isDisabled;
  return (
    <Menu>
      <MenuButton
        as={Box}
        p={2}
        width="100%"
        textAlign="left"
        borderRadius="md"
        borderWidth="1px"
        {...rest}
        {...(!isInvalid ? {} : { borderColor: 'red' })}
        disabled={isDisabled}
        onClick={isDisabled ? (e) => e.preventDefault() : rest.onClick}
        cursor={isDisabled ? 'not-allowed' : 'default'}
      >
        <Flex justifyContent="space-between" alignItems="center" gap={2}>
          <Text>{selectedIndex !== -1 ? options[selectedIndex]?.label : placeholder}</Text>
          <Text fontSize={10} color="gray.700">
            {'▼'}
          </Text>
        </Flex>
      </MenuButton>
      <ScrollableBox
        primary={false}
        as={MenuList}
        maxH={optionsListMaxHeight || '300px'}
        overflow={'auto'}
      >
        {options.map((item, index) => {
          const isItemDisabled = disabledOptions.includes(item.value);
          return (
            <MenuItem
              key={`CustomSelect-${index}-${item.value}`}
              value={item.value}
              onClick={() => (isItemDisabled ? undefined : onChange(item.value))}
              cursor={isItemDisabled ? 'not-allowed' : 'default'}
              color={isItemDisabled ? 'gray.300' : 'black'}
              _focus={{ bg: isItemDisabled ? 'white' : 'grey.300' }}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </ScrollableBox>
    </Menu>
  );
}

export default Dropdown;
